import React from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { findApp } from "@/data/apps";
import Reveal from "@/components/Reveal";
import CountUp from "@/components/CountUp";

function PriceTag({ price }) {
  const raw = String(price);
  const match = raw.match(/^([€$])?([\d.]+)(.*)$/);
  if (!match || Number.isNaN(Number(match[2]))) {
    return <span className="tag">{price}</span>;
  }
  const [, currency = "$", num, rest] = match;
  const decimals = num.includes(".") ? (num.split(".")[1]?.length || 2) : 0;
  return (
    <span className="tag">
      <CountUp to={Number(num)} prefix={currency} suffix={rest} decimals={decimals} duration={1100} />
    </span>
  );
}

function PricingCard({ plan, delay }) {
  return (
    <Reveal as="article" className="service service--pricing" delay={delay}>
      <div className="service__top">
        <PriceTag price={plan.price} />
        <h3>{plan.name}</h3>
      </div>
      {plan.badge && <span className="tag">{plan.badge}</span>}
      <p>{plan.note}</p>
    </Reveal>
  );
}

export default function AppLandingPage() {
  const { slug } = useParams();
  const app = findApp(slug);

  if (!app) {
    return <Navigate to="/apps" replace />;
  }

  const groups = app.pricingGroups;
  const flatPlans = app.pricing || [];
  const planCount = groups
    ? groups.reduce((n, g) => n + (g.plans?.length || 0), 0)
    : flatPlans.length;
  const paidPlans = flatPlans.filter((p) => /[$€][\d.]+/.test(p.price) || /\/mo/i.test(p.price));

  return (
    <section className="entity page-pad">
      <div className="wrap">
        <Reveal as="p" className="eyebrow">
          App / {app.practice} / {app.status}
        </Reveal>
        <Reveal as="h1" id="app-title" className="page-title" style={{ maxWidth: "16ch" }} delay={60}>
          {app.name}
        </Reveal>
        <Reveal as="p" className="entity__lead" delay={120}>
          {app.tagline}
        </Reveal>
        <Reveal as="p" className="entity__lead" delay={180}>
          {app.description}
        </Reveal>

        <Reveal className="page-metrics page-metrics--on-dark" delay={200}>
          <div className="page-metric">
            <strong>
              <CountUp to={(app.features || []).length} />
            </strong>
            <span>Key features</span>
          </div>
          <div className="page-metric">
            <strong>
              <CountUp to={planCount} />
            </strong>
            <span>Plans</span>
          </div>
          <div className="page-metric">
            <strong>
              <CountUp to={paidPlans.length || (groups ? groups.length - 1 : 0)} />
            </strong>
            <span>Paid modules</span>
          </div>
        </Reveal>

        <Reveal className="page-cta" style={{ marginTop: "1.5rem" }} delay={240}>
          {app.storeUrl && (
            <a className="btn" href={app.storeUrl} target="_blank" rel="noopener noreferrer">
              View on Shopify App Store
            </a>
          )}
          <Link className="btn btn--ghost" to="/contact">
            Talk to IT-Patrol
          </Link>
        </Reveal>

        <div className="entity__facts" style={{ marginTop: "2.5rem" }}>
          {(app.features || []).map((feature, i) => (
            <Reveal className="entity__fact" key={feature} delay={100 + i * 80}>
              <h2 style={{ fontSize: "1.05rem" }}>Feature</h2>
              <p>{feature}</p>
            </Reveal>
          ))}
        </div>

        {(groups?.length > 0 || flatPlans.length > 0) && (
          <div style={{ marginTop: "3rem" }}>
            <Reveal className="section__head">
              <p className="eyebrow">Pricing</p>
              <h2 className="page-title" style={{ fontSize: "clamp(1.6rem, 3vw, 2.2rem)" }}>
                Plans that scale with demand
              </h2>
              {app.pricingNote && (
                <p style={{ color: "rgba(255,255,255,0.62)", maxWidth: "54ch", marginTop: "0.75rem" }}>
                  {app.pricingNote}
                </p>
              )}
            </Reveal>

            {groups?.length > 0
              ? groups.map((group, gi) => (
                  <div key={group.title} style={{ marginTop: gi === 0 ? "1.5rem" : "2.5rem" }}>
                    <Reveal>
                      <h3
                        style={{
                          fontFamily: "var(--font-display)",
                          fontSize: "clamp(1.25rem, 2.2vw, 1.6rem)",
                          letterSpacing: "-0.03em",
                          marginBottom: "0.35rem",
                        }}
                      >
                        {group.title}
                      </h3>
                      {group.lead && (
                        <p style={{ color: "rgba(255,255,255,0.55)", marginBottom: "1rem", maxWidth: "48ch" }}>
                          {group.lead}
                        </p>
                      )}
                    </Reveal>
                    <div className="service-grid service-grid--auto">
                      {group.plans.map((plan, i) => (
                        <PricingCard key={plan.name} plan={plan} delay={120 + i * 90} />
                      ))}
                    </div>
                  </div>
                ))
              : (
                <div className="service-grid service-grid--auto">
                  {flatPlans.map((plan, i) => (
                    <PricingCard key={plan.name} plan={plan} delay={120 + i * 90} />
                  ))}
                </div>
              )}
          </div>
        )}

        <Reveal style={{ marginTop: "2rem" }} delay={160}>
          <Link to="/apps" style={{ textDecoration: "underline" }}>
            ← All apps
          </Link>
        </Reveal>
      </div>
    </section>
  );
}
